import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Row, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import { CreditCardOutlined, WalletOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { getActiveBrandId } from '../config/api';
import CreditService, { CreditBalance, CreditPackage, CreditTransaction } from '../services/creditService';

const { Title, Paragraph, Text } = Typography;

const Credits: React.FC = () => {
  const { user } = useAuth();
  const brandId = getActiveBrandId(user);
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const [balanceData, packageData, transactionData] = await Promise.all([
        CreditService.getBalance(brandId),
        CreditService.getPackages(),
        CreditService.getTransactions(brandId),
      ]);
      setBalance(balanceData);
      setPackages(packageData);
      setTransactions(transactionData);
    } catch (error: any) {
      message.error(error.message || 'Kredi bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const startPurchase = async (packageId: string) => {
    if (!brandId) return;
    try {
      const result = await CreditService.createPurchase(brandId, packageId);
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      message.info(result.message || 'Satın alma kaydı oluşturuldu. iyzico checkout yakında bağlanacak.');
      await loadData();
    } catch (error: any) {
      message.error(error.message || 'Satın alma başlatılamadı');
    }
  };

  return (
    <div>
      <Title level={2}>Krediler</Title>
      <Paragraph type="secondary">Studio üretimleri için marka kredi bakiyenizi ve iyzico paketlerinizi yönetin.</Paragraph>

      {!brandId && <Alert type="warning" showIcon message="Bu kullanıcıya bağlı marka bulunamadı" />}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <Card loading={loading}>
            <Statistic title="Kullanılabilir Kredi" value={balance?.availableCredits ?? 0} prefix={<WalletOutlined />} suffix={balance?.currency || 'TRY'} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card loading={loading}>
            <Statistic title="Rezerve Kredi" value={balance?.reservedCredits ?? 0} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card loading={loading}>
            <Statistic title="Toplam Harcanan" value={balance?.lifetimeSpentCredits ?? 0} />
          </Card>
        </Col>
      </Row>

      <Title level={3}>Kredi Paketleri</Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {packages.map((pkg) => (
          <Col xs={24} md={8} key={pkg.id}>
            <Card
              title={pkg.name}
              extra={<Tag color="green">{pkg.currency}</Tag>}
              actions={[<Button type="primary" icon={<CreditCardOutlined />} onClick={() => startPurchase(pkg.id)}>iyzico ile Satın Al</Button>]}
            >
              <Space direction="vertical">
                <Text strong style={{ fontSize: 24 }}>{pkg.credits} kredi</Text>
                <Text>{Number(pkg.priceAmount).toLocaleString('tr-TR')} {pkg.currency}</Text>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      <Title level={3}>Kredi Hareketleri</Title>
      <Table
        loading={loading}
        dataSource={transactions}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        columns={[
          { title: 'Tarih', dataIndex: 'createdAt', render: (value) => value ? new Date(value).toLocaleString('tr-TR') : '-' },
          { title: 'Tip', dataIndex: 'type', render: (value) => <Tag color={value === 'spend' ? 'red' : value === 'refund' ? 'blue' : 'green'}>{value}</Tag> },
          { title: 'Feature', render: (_, row) => row.featureType?.name || row.referenceType || '-' },
          { title: 'Miktar', dataIndex: 'amount', render: (value) => <Text type={value < 0 ? 'danger' : 'success'}>{value > 0 ? `+${value}` : value}</Text> },
          { title: 'Bakiye Sonrası', dataIndex: 'balanceAfter' },
          { title: 'Durum', dataIndex: 'status' },
        ]}
      />
    </div>
  );
};

export default Credits;
